
import { ValueType } from '../../../../Utils/Formatters/FormatterTypes';
import ARBusinessConfig from '../../Anläggning/ARBusinessConfig';
import BOBusinessConfig from '../../Bokning/BOBusinessConfig';
import DIBusinessConfig from '../../Diarie/DIBusinessConfig';
import EKBusinessConfig from '../../Ekonomi/EKBusinessConfig';
import GABusinessConfig from '../../Grav/GABusinessConfig';
import KJBusinessConfig from '../../Kremationsjournal/KJBusinessConfig';
import KRBusinessConfig from '../../Kundreskontra/KRBusinessConfig';
import LABusinessConfig from '../../Lager/LABusinessConfig';
import LRBusinessConfig from '../../Leverantor/LRBusinessConfig';
import MUBusinessConfig from '../../Musik/MUBusinessConfig';
import OPBusinessConfig from '../../OP/OPBusinessConfig';
import OFBusinessConfig from '../../OrderFakt/OFBusinessConfig';
import PUBusinessConfig from '../../PU/PUBusinessConfig';
import SYSBusinessConfig from '../../SystemAdministration/SYSBusinessConfig';
import TIBusinessConfig from '../../Tidredovisning/TIBusinessConfig';
import { initConst } from '../../../../Utils/utils';
import { mergeAll, mergeDeepLeft, uniq } from 'ramda';
import {
  ISortOrder,
  ISortInfo,
} from '../../../../BaseComponents/Table/Components/SortableTableUtils';
import { IColumnDef } from '../../../../Types/ColumnDefType';
import { ICollectionDataType } from '../Components/BusinessCollectionComponents/Queries/BusinessCollectionQuery';
import { CommonPropsAndTypes } from '../../../../BaseComponents/_Types/StandardComponentProps';
import { IFormData } from '../Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { AttributeValueSizeType } from '../../../../Components/Attributes/AttributeValue/AttributeValue';
import { rutiner } from './Rutiner';
import { styrvarden } from './Styrvarden';
import { Breakpoint } from '../../../../BaseComponents/ThemeProvider/AvenyTheme';
import { BusinessMenuButtons } from './Delsystem';
import { IUseItemFunctionMenuItemsTypeInput } from '../../../../Utils/ObjektVyUtils';
import { DocumentNode } from 'graphql';
import { Location } from 'history';
import {
  Breakpointsconfig,
  findBreakpointValue,
} from '../../../../Components/BreakpointContext/BreakpointContextUtils';

export type DisplayCompType = React.ComponentType<
  React.PropsWithChildren<{
    value: ValueType;
    displayValue: string;
  }>
>;

export type AddCompType = React.ComponentType<
  React.PropsWithChildren<{
    onCancel?: () => void;
    onValuesCommited?: (skipRefetch?: boolean) => void;
    title: string;
    open: boolean;
    onClose: () => void;
    onAddNewItem?: (newItem: IFormData) => void;
  }>
>;

interface IAttributeConfig {
  displayComp?: DisplayCompType;
  /** Min width when shown in table */
  tableMinWidth?: number;
  /** Default width when shown in table */
  tableDefaultWidth?: number | { [K in Breakpoint]?: number };
  size?: AttributeValueSizeType;
  hideTooltip?: boolean;
  /** Rita inte ut label bredvid värde när designen kräver att plats sparas, t.ex. i kort */
  optionalLabel?: boolean;
}
export interface IAssociationConfig {
  /** Ta bort ikonen för att välja värden enligt association, kan stängas av om listan på möjliga värden i sin helhet är ointressant t.ex. på grund av att det är väldigt många möjliga värden */
  disableArrowIcon?: boolean;
}
export interface IBusinessConfigDefaultSortInfo {
  attributeName: string;
  order: ISortOrder;
}
export interface IBusinessColorConfig {
  caption: string;
  color?: CommonPropsAndTypes.LegendColorType;
  colorOpacity?: 'faded' | 'full';
  condition: (data: any) => boolean;
  dataDependencies: string[];
  striped?: boolean;
  greyBox?: boolean;
}

export interface IBusinessStylesConfig {
  styles: IBusinessColorConfig[];
  styleCard?: boolean;
  hideLegendColumn?: boolean;
}

/** Funktion som anger om ett val på en funktionsmeny ska vara disabled */
export type TItemFunctionMenuDisableFuntion = (
  input: IUseItemFunctionMenuItemsTypeInput
) => {
  disabled: boolean;
  disabledReason?: string;
};
export interface IDefaultColumnDef extends IColumnDef {
  minSize?: Breakpoint;
}
export interface IBusinessConfig {
  listIcons?: IBusinessListIconDef[];
  colors?: IBusinessStylesConfig;
  /**Attribut som i sig visar färgerna, om det visas du ersätter det colors */
  attributeWithColors?: string;
  attributes?: { [attributeName: string]: IAttributeConfig };
  associations?: { [associationName: string]: IAssociationConfig };
  addComponent?: AddCompType;
  defaultSort?: IBusinessConfigDefaultSortInfo[];
  defaultColumns: IDefaultColumnDef[];
  unusedBusinessMenuButtons?: BusinessMenuButtons[];
  dataDependencies?: string[];
  breadcrumbsLabel?: (location: Location) => string;
  //** Användaren kan ta bort objekt med standardfunktionalitet */
  userDeleteable?: boolean;
  /** Om userDeletable är satt så kan den här funktionen användas för att tillåta borttag/ångra borttag per objekt */
  itemDeletable?: TItemFunctionMenuDisableFuntion;
  objectLinksEnabled?: boolean;
  /** List of query documents or names that will be refetched when the refresh button on the collection menu is pressed */
  refetchButtonQueries?: Array<DocumentNode | string>;
}
export type ICustomBusinessConfig = Partial<IBusinessConfig>;
export interface IBusinessListIconDef {
  componentFn: BusinessIconListComponentFnType;
  dataDependencies: string[];
}
export type BusinessIconListComponentFnType = (
  config: IBusinessIconListConfig
) => React.ReactNode;
export interface IBusinessIconListConfig {
  id?: string;
  data: ICollectionDataType & { [key: string]: any };
  disabled?: boolean;
}
export interface IBusinessConfigs {
  readonly [collectionName: string]: IBusinessConfig;
}
export interface ICustomBusinessConfigs {
  readonly [collectionName: string]: ICustomBusinessConfig;
}
export const businessConfigs: () => IBusinessConfigs =
  initConst<IBusinessConfigs>(() =>
    mergeAll([
      ARBusinessConfig(),
      BOBusinessConfig(),
      DIBusinessConfig(),
      EKBusinessConfig(),
      GABusinessConfig(),
      KJBusinessConfig(),
      KRBusinessConfig(),
      LABusinessConfig(),
      LRBusinessConfig(),
      MUBusinessConfig(),
      OPBusinessConfig(),
      OFBusinessConfig(),
      PUBusinessConfig(),
      SYSBusinessConfig(),
      TIBusinessConfig(),
    ])
  );

export const findDisplayComp = (
  collectionName: string,
  attributeName: string
): DisplayCompType | undefined => {
  const businessConfig = businessConfigs()[collectionName];
  const attributeConfig =
    businessConfig &&
    businessConfig.attributes &&
    businessConfig.attributes[attributeName];
  const displayComp = attributeConfig && attributeConfig.displayComp;
  return displayComp;
};

export const findAttributeSize = (
  collectionName: string,
  attributeName: string
): AttributeValueSizeType | undefined => {
  const businessConfig = businessConfigs()[collectionName];
  const attributeConfig =
    businessConfig &&
    businessConfig.attributes &&
    businessConfig.attributes[attributeName];
  const size = attributeConfig && attributeConfig.size;
  return size;
};

export const findAttributeHideTooltip = (
  collectionName: string,
  attributeName: string
): boolean | undefined => {
  const businessConfig = businessConfigs()[collectionName];
  const attributeConfig =
    businessConfig &&
    businessConfig.attributes &&
    businessConfig.attributes[attributeName];
  const hideTooltip = attributeConfig && attributeConfig.hideTooltip;
  return hideTooltip;
};

export const findOptionalLabel = (
  collectionName: string,
  attributeName: string
): boolean | undefined =>
  findBusinessconfig(collectionName)?.attributes?.[attributeName]
    ?.optionalLabel;

// Ingen inställning är obligatorisk så vi kan alltid använda ett tomt objekt som default för att slippa hantera undefined
const defaultAssociationConfig = {};

export const findAssociationConfig = (
  collectionName: string,
  associationName: string
): IAssociationConfig => {
  const businessConfig = findBusinessconfig(collectionName);
  const associationConfig = businessConfig?.associations?.[associationName];
  return associationConfig ?? defaultAssociationConfig;
};

export const findAddComp = (
  collectionName: string
): AddCompType | undefined => {
  const businessConfig = businessConfigs()[collectionName];
  const addComp = businessConfig && businessConfig.addComponent;
  return addComp;
};

export const tryFindDefaultSort = (
  collectionName: string,
  rutinKod: string
): IBusinessConfigDefaultSortInfo[] | undefined => {
  const rutin = rutiner[rutinKod] ?? styrvarden()?.[rutinKod];
  const defaultSortInRutin = rutin?.config?.[collectionName]?.defaultSort;
  const businessConfig = businessConfigs()[collectionName];
  const defaultSortProperties = businessConfig && businessConfig.defaultSort;

  if (defaultSortInRutin !== undefined) {
    return defaultSortInRutin;
  } else {
    return defaultSortProperties;
  }
};

export const findBusinessconfig = (
  collectionName: string
): IBusinessConfig | undefined => businessConfigs()[collectionName];

export const findBusinessColors = (
  collectionName: string
): IBusinessColorConfig[] => {
  const config = businessConfigs()[collectionName];
  return config?.colors?.styles ?? [];
};

export const findBusinessStyleConfig = (
  collectionName: string
): IBusinessStylesConfig | undefined => {
  const config = businessConfigs()[collectionName];
  return config?.colors;
};

export const findBusinessDataDependencies = (
  collectionName: string
): string[] => {
  const config = businessConfigs()[collectionName];
  return config?.dataDependencies ?? [];
};

export const filterBusinessColors = (
  colors: IBusinessColorConfig[],
  sortInfo: ISortInfo<ICollectionDataType>
): IBusinessColorConfig[] => {
  const colorUsed =
    sortInfo.rows.length === 0 ||
    sortInfo.rows.some(a => colors.some(x => x.condition(a)));
  return colorUsed ? colors : [];
};

export const findBusinessMenuUnusedButtons = (
  collectionName: string
): BusinessMenuButtons[] | undefined => {
  return businessConfigs()[collectionName]?.unusedBusinessMenuButtons;
};

export const findBreadcrumbsLabel = (
  collectionName: string
): ((location: Location) => string) | undefined => {
  return businessConfigs()[collectionName]?.breadcrumbsLabel;
};

export const findTableMinWidth = (
  collectionName: string,
  attributeName: string
): number | undefined => {
  const businessConfig = businessConfigs()[collectionName];
  const attributeConfig =
    businessConfig &&
    businessConfig.attributes &&
    businessConfig.attributes[attributeName];
  const tableMinWidth = attributeConfig && attributeConfig.tableMinWidth;
  return tableMinWidth;
};

export const findTableDefaultWidth = (
  collectionName: string,
  attributeName: string,
  breakpoint: Breakpoint | undefined
): number | undefined => {
  const businessConfig = businessConfigs()[collectionName];
  const attributeConfig =
    businessConfig &&
    businessConfig.attributes &&
    businessConfig.attributes[attributeName];
  const tableDefaultWidth: Breakpointsconfig<number | undefined> =
    attributeConfig && attributeConfig.tableDefaultWidth;
  return findBreakpointValue<number | undefined>(tableDefaultWidth, breakpoint);
};

export const addAllDataDependencies = (
  collectionName: string,
  columns: readonly IColumnDef[],
  association?: string,
  customBusinessConfig?: ICustomBusinessConfig
): string[] => {
  const businessConfig =
    customBusinessConfig != undefined
      ? mergeDeepLeft(
          customBusinessConfig,
          findBusinessconfig(collectionName) ?? {}
        )
      : findBusinessconfig(collectionName);

  const listIcons = businessConfig?.listIcons ?? [];
  const listColors = businessConfig?.colors?.styles ?? [];
  const businessDataDependencies = businessConfig?.dataDependencies ?? [];
  const listIconDataDep =
    listIcons.flatMap(icon =>
      icon.dataDependencies.map(attr =>
        association ? `${association}.${attr}` : attr
      )
    ) ?? [];
  const listColorDataDep =
    listColors.flatMap(color =>
      color.dataDependencies.map(attr =>
        association ? `${association}.${attr}` : attr
      )
    ) ?? [];
  const businessDataDep = businessDataDependencies.map(attr =>
    association ? `${association}.${attr}` : attr
  );
  const columnsDataDep = columns.map(col => {
    const attributeDef = col.association
      ? `${col.association}.${col.attributeDef || 'IDString'}`
      : col.attributeDef;
    return association ? `${association}.${attributeDef}` : attributeDef;
  });
  const attributeDefs = [
    ...columnsDataDep,
    ...listIconDataDep,
    ...listColorDataDep,
    ...businessDataDep,
  ];
  return uniq(attributeDefs);
};

export const findHeaderColumns = (collectionName: string) => {
  const defaultHeader = ['IDString'];
  const config = findBusinessconfig(collectionName);
  if (!config) {
    return defaultHeader;
  }
  const headers = config.defaultColumns.reduce<string[]>((acc, x) => {
    if (x.header) {
      acc.push(x.attributeDef);
    }
    return acc;
  }, []);
  return headers.length > 0 ? headers : defaultHeader;
};
